import React, { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../supabase'
import { useTheme } from '../context/ThemeContext'
import LoanList from './LoanList'
import AddLoanModal from './AddLoanModal'
import Summary from './Summary'
import PaymentHistory from './PaymentHistory'
import LoanComparison from './LoanComparison'
import Reminders from './Reminders'
import SmartPayoffEngine from './SmartPayoffEngine'
import HealthAndSuggestions from './HealthAndSuggestions'
import Profile from './Profile'
import InsuranceList from './InsuranceList'
import AddInsuranceModal from './AddInsuranceModal'
import LoanCalculator from './LoanCalculator'
import CreditScoreTips from './CreditScoreTips'
import BudgetPlanner from './BudgetPlanner'
import Expenses from './Expenses'
import EmergencyFund from './EmergencyFund'
import Investments from './Investments'
import NetWorth from './NetWorth'
import MembershipCard from './MembershipCard'
import { exportPDF, exportExcel, exportInterestCertificate } from '../utils/exportData'
import Sidebar from './Sidebar'
import Forum from './Forum'
import ClubhouseBell from './ClubhouseBell'
import ActionButton from './ActionButton'
import OnboardingPrompt from './OnboardingPrompt'
import { useCurrency } from '../context/CurrencyContext'
import { CURRENCIES } from '../utils/currency'

export default function Dashboard({ session, onOpenAdmin, appSettings = { app_name: 'Debt Crush', primary_color: '#c9a84c' } }) {
  const [profile, setProfile] = useState(null)
  const [loans, setLoans] = useState([])
  const [isPaid, setIsPaid] = useState(false)
  const [showExportUpsell, setShowExportUpsell] = useState(false)
  const [showLoanLimitModal, setShowLoanLimitModal] = useState(false)
  const [featureLock, setFeatureLock] = useState(null)
  const [giftPopup, setGiftPopup] = useState(null) // { message, months }
  const [emiPayments, setEmiPayments] = useState([])
  const [activePage, setActivePage] = useState('dashboard')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingLoan, setEditingLoan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [insurancePolicies, setInsurancePolicies] = useState([])
  const [insurancePayments, setInsurancePayments] = useState([])
  const [insuranceClaims, setInsuranceClaims] = useState([])
  const [showAddInsuranceModal, setShowAddInsuranceModal] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [expenses, setExpenses] = useState([])
  const [expensePayments, setExpensePayments] = useState([])
  const [emergencyFund, setEmergencyFund] = useState(null)
  const [emergencyFundTx, setEmergencyFundTx] = useState([])
  const [investments, setInvestments] = useState([])
  const [investmentHistory, setInvestmentHistory] = useState([])
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('dc_sidebar_collapsed')
      if (stored !== null) return stored === 'true'
      return typeof window !== 'undefined' && window.innerWidth < 900
    } catch { return false }
  })
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.innerWidth <= 768 } catch { return false }
  })
  const [messages, setMessages] = useState([])
  const [dismissedMsgs, setDismissedMsgs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dc_dismissed_msgs') || '[]') } catch { return [] }
  })
  const [showNotifications, setShowNotifications] = useState(false)
  const exportRef = useRef(null)
  const notifRef = useRef(null)
  const { isDark, toggleTheme } = useTheme()
  const { currency, setCurrency } = useCurrency()

  const currentFY = (() => {
    const n = new Date()
    const y = n.getFullYear()
    return n.getMonth() >= 3 ? `${y}-${String(y + 1).slice(2)}` : `${y - 1}-${String(y).slice(2)}`
  })()
  const fyOptions = [currentFY, (() => { const p = currentFY.split('-'); return `${parseInt(p[0]) - 1}-${p[0].slice(2)}` })()]

  const now = new Date()
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

  const fetchProfile = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()
    if (data) setProfile(data)
  }, [session.user.id])

  const fetchLoans = useCallback(async () => {
    const { data, error } = await supabase
      .from('loans')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (error) {
      console.error('Fetch loans error:', error.message)
      setLoading(false)
      return
    }
    if (data) setLoans(data)
    setLoading(false)
  }, [session.user.id])

  const fetchEmiPayments = useCallback(async () => {
    const { data } = await supabase
      .from('emi_payments')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setEmiPayments(data)
  }, [session.user.id])

  const fetchInsurancePolicies = useCallback(async () => {
    const { data } = await supabase
      .from('insurance_policies')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setInsurancePolicies(data)
  }, [session.user.id])

  const fetchInsurancePayments = useCallback(async () => {
    const { data } = await supabase
      .from('insurance_payments')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setInsurancePayments(data)
  }, [session.user.id])

  const fetchInsuranceClaims = useCallback(async () => {
    const { data } = await supabase
      .from('insurance_claims')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setInsuranceClaims(data)
  }, [session.user.id])

  const fetchExpenses = useCallback(async () => {
    const { data } = await supabase
      .from('expense_categories')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setExpenses(data)
  }, [session.user.id])

  const fetchExpensePayments = useCallback(async () => {
    const { data } = await supabase
      .from('expense_payments')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setExpensePayments(data)
  }, [session.user.id])

  async function saveExpense(data) {
    if (data.id) {
      const { id, ...fields } = data
      const { error } = await supabase
        .from('expense_categories')
        .update(fields)
        .eq('id', id)
        .eq('user_id', session.user.id)
      if (error) { alert('Error updating expense: ' + error.message); return }
    } else {
      const { error } = await supabase
        .from('expense_categories')
        .insert({ ...data, user_id: session.user.id })
      if (error) { alert('Error saving expense: ' + error.message); return }
    }
    fetchExpenses()
  }

  async function deleteExpense(expenseId) {
    if (!window.confirm('Remove this expense?')) return
    const { error } = await supabase
      .from('expense_categories')
      .delete()
      .eq('id', expenseId)
      .eq('user_id', session.user.id)
    if (error) { alert('Error: ' + error.message); return }
    setExpenses(prev => prev.filter(e => e.id !== expenseId))
  }

  async function setExpensePayment(expenseId, status) {
    const expense = expenses.find(e => e.id === expenseId)
    if (!expense) return

    const existing = expensePayments.find(
      p => p.expense_id === expenseId && p.payment_month === currentMonth
    )

    if (existing && existing.status === status) {
      await supabase.from('expense_payments').delete().eq('id', existing.id)
    } else if (existing) {
      await supabase.from('expense_payments').update({
        status,
        amount_paid: status === 'paid' ? parseFloat(expense.amount) : 0,
        paid_at: status === 'paid' ? new Date().toISOString() : null,
      }).eq('id', existing.id)
    } else {
      await supabase.from('expense_payments').insert({
        expense_id:    expenseId,
        user_id:       session.user.id,
        payment_month: currentMonth,
        status,
        amount_paid:   status === 'paid' ? parseFloat(expense.amount) : 0,
        paid_at:       status === 'paid' ? new Date().toISOString() : null,
      })
    }
    fetchExpensePayments()
  }

  const fetchEmergencyFund = useCallback(async () => {
    const { data } = await supabase
      .from('emergency_fund')
      .select('*')
      .eq('user_id', session.user.id)
      .maybeSingle()
    if (data) setEmergencyFund(data)
  }, [session.user.id])

  const fetchEmergencyFundTx = useCallback(async () => {
    const { data } = await supabase
      .from('emergency_fund_transactions')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setEmergencyFundTx(data)
  }, [session.user.id])

  async function saveEmergencyFund(fundData) {
    if (emergencyFund?.id) {
      const { error } = await supabase
        .from('emergency_fund')
        .update(fundData)
        .eq('id', emergencyFund.id)
        .eq('user_id', session.user.id)
      if (error) { alert('Error saving fund settings: ' + error.message); return }
    } else {
      const { error } = await supabase
        .from('emergency_fund')
        .insert({ ...fundData, user_id: session.user.id, current_balance: 0 })
      if (error) { alert('Error saving fund settings: ' + error.message); return }
    }
    fetchEmergencyFund()
  }

  async function addEmergencyFundTransaction({ type, amount, notes }) {
    const { error: txError } = await supabase
      .from('emergency_fund_transactions')
      .insert({ user_id: session.user.id, type, amount, notes })
    if (txError) { alert('Error recording transaction: ' + txError.message); return }

    const currentBalance = parseFloat(emergencyFund?.current_balance || 0)
    const newBalance = type === 'deposit'
      ? currentBalance + amount
      : Math.max(0, currentBalance - amount)

    if (emergencyFund?.id) {
      await supabase.from('emergency_fund')
        .update({ current_balance: newBalance })
        .eq('id', emergencyFund.id)
        .eq('user_id', session.user.id)
    } else {
      await supabase.from('emergency_fund')
        .insert({ user_id: session.user.id, current_balance: newBalance, target_months: 6, monthly_contribution: 0 })
    }
    fetchEmergencyFund()
    fetchEmergencyFundTx()
  }

  const fetchInvestments = useCallback(async () => {
    const { data } = await supabase
      .from('investments')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setInvestments(data)
  }, [session.user.id])

  const fetchInvestmentHistory = useCallback(async () => {
    const { data } = await supabase
      .from('investment_history')
      .select('*')
      .eq('user_id', session.user.id)
      .order('recorded_at', { ascending: false })
    if (data) setInvestmentHistory(data)
  }, [session.user.id])

  async function saveInvestment(data) {
    if (data.id) {
      const { id, ...fields } = data
      const { error } = await supabase
        .from('investments')
        .update(fields)
        .eq('id', id)
        .eq('user_id', session.user.id)
      if (error) { alert('Error updating investment: ' + error.message); return }
    } else {
      const { error } = await supabase
        .from('investments')
        .insert({ ...data, user_id: session.user.id })
      if (error) { alert('Error saving investment: ' + error.message); return }
    }
    fetchInvestments()
  }

  async function deleteInvestment(investmentId) {
    if (!window.confirm('Remove this investment?')) return
    const { error } = await supabase
      .from('investments')
      .delete()
      .eq('id', investmentId)
      .eq('user_id', session.user.id)
    if (error) { alert('Error: ' + error.message); return }
    setInvestments(prev => prev.filter(i => i.id !== investmentId))
  }

  async function updateInvestmentValue(investmentId, value, notes) {
    const { error: histError } = await supabase
      .from('investment_history')
      .insert({
        investment_id: investmentId,
        user_id:       session.user.id,
        value,
        notes,
        recorded_at:   new Date().toISOString(),
      })
    if (histError) { alert('Error recording history: ' + histError.message); return }
    const { error } = await supabase
      .from('investments')
      .update({ current_value: value })
      .eq('id', investmentId)
      .eq('user_id', session.user.id)
    if (error) { alert('Error updating value: ' + error.message); return }
    fetchInvestments()
    fetchInvestmentHistory()
  }

  async function updateInvestmentCurrentValue(investmentId, value) {
    const { error } = await supabase
      .from('investments')
      .update({ current_value: value })
      .eq('id', investmentId)
      .eq('user_id', session.user.id)
    if (!error) {
      setInvestments(prev => prev.map(i =>
        i.id === investmentId ? { ...i, current_value: value } : i
      ))
    }
  }

  const fetchMessages = useCallback(async () => {
    const { data } = await supabase
      .from('admin_messages')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
    if (data) setMessages(data)
  }, [])

  useEffect(() => {
    supabase.from('profiles').update({ last_seen_at: new Date().toISOString() }).eq('id', session.user.id)
    supabase.from('memberships').select('is_paid, gift_expires_at, gift_notified').eq('user_id', session.user.id).single()
      .then(async ({ data }) => {
        if (!data) return
        const now = new Date()
        const isGifted = data.gift_expires_at && new Date(data.gift_expires_at) > now
        const isExpired = data.gift_expires_at && new Date(data.gift_expires_at) <= now

        // Auto-revoke expired gift
        if (isExpired) {
          await supabase.from('memberships').update({ is_paid: false, gift_expires_at: null, gift_notified: false }).eq('user_id', session.user.id)
          await supabase.from('profiles').update({ role: 'user' }).eq('id', session.user.id)
          setIsPaid(false)
          return
        }

        setIsPaid(!!data.is_paid)

        // Show gift popup if not yet shown
        if (isGifted && !data.gift_notified) {
          const expiry = new Date(data.gift_expires_at)
          const expiryLabel = expiry.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
          setGiftPopup({ message: `You have been gifted DebtCrush Premium! Enjoy all Premium features until ${expiryLabel}.` })
          await supabase.from('memberships').update({ gift_notified: true }).eq('user_id', session.user.id)
        }
      })
    fetchProfile()
    fetchLoans()
    fetchEmiPayments()
    fetchMessages()
    fetchInsurancePolicies()
    fetchInsurancePayments()
    fetchInsuranceClaims()
    fetchExpenses()
    fetchExpensePayments()
    fetchEmergencyFund()
    fetchEmergencyFundTx()
    fetchInvestments()
    fetchInvestmentHistory()
  }, [session.user.id, fetchProfile, fetchLoans, fetchEmiPayments, fetchMessages, fetchInsurancePolicies, fetchInsurancePayments, fetchInsuranceClaims, fetchExpenses, fetchExpensePayments, fetchEmergencyFund, fetchEmergencyFundTx, fetchInvestments, fetchInvestmentHistory])

  function dismissMessage(id) {
    const updated = [...dismissedMsgs, id]
    setDismissedMsgs(updated)
    localStorage.setItem('dc_dismissed_msgs', JSON.stringify(updated))
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  async function saveLoan(loanData) {
    if (editingLoan) {
      const { error } = await supabase
        .from('loans')
        .update({
          name: loanData.name,
          type: loanData.type,
          balance: parseFloat(loanData.balance),
          original_amount: parseFloat(loanData.original_amount),
          interest_rate: parseFloat(loanData.interest_rate),
          emi_amount: parseFloat(loanData.emi_amount),
          due_day: loanData.due_day,
          start_date: loanData.start_date || null,
          end_date: loanData.end_date || null,
        })
        .eq('id', editingLoan.id)
        .eq('user_id', session.user.id)
      if (error) {
        console.error('Update error:', error.message)
        alert('Error updating loan: ' + error.message)
        return
      }
    } else {
      const { error } = await supabase
        .from('loans')
        .insert({ ...loanData, user_id: session.user.id })
      if (error) {
        console.error('Insert error:', error.message)
        alert('Error saving loan: ' + error.message)
        return
      }
    }

    const { data, error: fetchError } = await supabase
      .from('loans')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })

    if (fetchError) {
      console.error('Fetch error:', fetchError.message)
    } else {
      setLoans(data || [])
    }

    setEditingLoan(null)
    setShowAddModal(false)
  }

  async function deleteLoan(loanId) {
    if (!window.confirm('Remove this loan?')) return
    const { error } = await supabase
      .from('loans')
      .delete()
      .eq('id', loanId)
      .eq('user_id', session.user.id)
    if (error) {
      console.error('Delete error:', error.message)
      alert('Error: ' + error.message)
      return
    }
    setLoans(prev => prev.filter(l => l.id !== loanId))
  }

  async function setEmiStatus(loanId, status) {
    const loan = loans.find(l => l.id === loanId)
    if (!loan) return

    const existing = emiPayments.find(
      p => p.loan_id === loanId && p.payment_month === currentMonth
    )

    if (existing && existing.status === status) {
      await supabase.from('emi_payments').delete().eq('id', existing.id)
      if (status === 'paid') {
        await supabase.from('loans').update({
          balance: parseFloat(loan.balance) + parseFloat(loan.emi_amount)
        }).eq('id', loanId)
      }
    } else {
      if (existing && existing.status === 'paid') {
        await supabase.from('loans').update({
          balance: parseFloat(loan.balance) + parseFloat(loan.emi_amount)
        }).eq('id', loanId)
      }
      if (existing) {
        await supabase.from('emi_payments').update({
          status,
          amount_paid: status === 'paid' ? loan.emi_amount : 0,
          paid_at: status === 'paid' ? new Date().toISOString() : null
        }).eq('id', existing.id)
      } else {
        await supabase.from('emi_payments').insert({
          loan_id: loanId,
          user_id: session.user.id,
          payment_month: currentMonth,
          status,
          amount_paid: status === 'paid' ? loan.emi_amount : 0,
          paid_at: status === 'paid' ? new Date().toISOString() : null
        })
      }
      if (status === 'paid') {
        await supabase.from('loans').update({
          balance: Math.max(0, parseFloat(loan.balance) - parseFloat(loan.emi_amount))
        }).eq('id', loanId)
      }
    }

    fetchLoans()
    fetchEmiPayments()
  }

  async function savePolicy(policyData) {
    if (editingPolicy) {
      const { error } = await supabase
        .from('insurance_policies')
        .update(policyData)
        .eq('id', editingPolicy.id)
        .eq('user_id', session.user.id)
      if (error) { alert('Error updating policy: ' + error.message); return }
    } else {
      const { error } = await supabase
        .from('insurance_policies')
        .insert({ ...policyData, user_id: session.user.id })
      if (error) { alert('Error saving policy: ' + error.message); return }
    }
    setEditingPolicy(null)
    setShowAddInsuranceModal(false)
    fetchInsurancePolicies()
  }

  async function deletePolicy(policyId) {
    if (!window.confirm('Remove this insurance policy?')) return
    const { error } = await supabase
      .from('insurance_policies')
      .delete()
      .eq('id', policyId)
      .eq('user_id', session.user.id)
    if (error) { alert('Error: ' + error.message); return }
    setInsurancePolicies(prev => prev.filter(p => p.id !== policyId))
  }

  async function setInsurancePaymentStatus(policyId, status) {
    const existing = insurancePayments.find(
      p => p.policy_id === policyId && p.payment_month === currentMonth
    )
    if (existing && existing.status === status) {
      await supabase.from('insurance_payments').delete().eq('id', existing.id)
    } else if (existing) {
      await supabase.from('insurance_payments').update({
        status,
        paid_at: status === 'paid' ? new Date().toISOString() : null
      }).eq('id', existing.id)
    } else {
      await supabase.from('insurance_payments').insert({
        policy_id: policyId,
        user_id: session.user.id,
        payment_month: currentMonth,
        status,
        paid_at: status === 'paid' ? new Date().toISOString() : null
      })
    }
    fetchInsurancePayments()
  }

  useEffect(() => {
    function handleClickOutside(e) {
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExportMenu(false)
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const unreadMessages = messages.filter(m => !dismissedMsgs.includes(m.id))
  const unreadCount = unreadMessages.length

  const userName = profile?.full_name ||
    session?.user?.user_metadata?.full_name ||
    session?.user?.user_metadata?.name ||
    'User'

  const userAvatar = session?.user?.user_metadata?.avatar_url

  const PAGE_TITLES = {
    dashboard: 'Dashboard', loans: 'Loans', insurance: 'Insurance',
    expenses: 'Expenses', investments: 'Investments', networth: 'Net Worth',
    emergency: 'Emergency Fund', calculator: 'EMI Calculator',
    budget: 'Budget Planner', credit: 'Credit Score Tips',
    history: 'Payment History', compare: 'Loan Comparison',
    reminders: 'Reminders', profile: 'Profile',
    payoff: 'Smart Payoff Engine',
  }

  const showAdmin = profile?.role === 'admin' || profile?.role === 'superadmin'
  const W = sidebarCollapsed ? 60 : 240

  function toggleSidebar() {
    const next = !sidebarCollapsed
    setSidebarCollapsed(next)
    try { localStorage.setItem('dc_sidebar_collapsed', String(next)) } catch {}
  }

  const menuBtnStyle = { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '0.65rem 1rem', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer', color: 'var(--text)', transition: 'background 0.12s' }

  const isAdmin = profile?.role === 'admin' || profile?.role === 'superadmin' || profile?.role === 'gifted'
  const exportLocked = !isPaid && !isAdmin

  function handleExportClick(action) {
    setShowExportMenu(false)
    if (exportLocked) { setShowExportUpsell(true); return }
    action()
  }

  const exportMenuContent = (
    <>
      <button style={menuBtnStyle}
        onClick={() => handleExportClick(() => exportPDF(loans, emiPayments, userName))}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hdr)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >📄 Export as PDF{exportLocked && ' 🔒'}</button>
      <div style={{ height: '1px', background: 'var(--border)' }} />
      <button style={menuBtnStyle}
        onClick={() => handleExportClick(() => exportExcel(loans, emiPayments, userName))}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hdr)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >📊 Export as Excel{exportLocked && ' 🔒'}</button>
      <div style={{ height: '1px', background: 'var(--border)' }} />
      {fyOptions.map(fy => (
        <button key={fy} style={menuBtnStyle}
          onClick={() => handleExportClick(() => exportInterestCertificate(loans, emiPayments, userName, fy))}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hdr)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >🧾 Interest Cert FY{fy}{exportLocked && ' 🔒'}</button>
      ))}
    </>
  )

  const exportUpsellModal = showExportUpsell && (
    <div
      onClick={e => { if (e.target === e.currentTarget) setShowExportUpsell(false) }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: '#0d0d0d', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', width: '100%', maxWidth: '380px', padding: '1.5rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.6rem' }}>🔒</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: '#f5f0e8', marginBottom: '0.5rem' }}>
          Export is a Premium feature
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '0.74rem', color: 'rgba(245,240,232,0.65)', lineHeight: 1.6, marginBottom: '1.1rem' }}>
          Upgrade to DebtCrush Premium (₹149/month) to export your loan data as PDF, Excel, or an interest certificate.
        </div>
        <button
          onClick={() => { setShowExportUpsell(false); setActivePage('dashboard') }}
          style={{ background: '#c9a84c', color: '#0d0d0d', border: 'none', padding: '0.55rem 1.3rem', borderRadius: '5px', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.03em', marginRight: '0.5rem' }}
        >
          View Membership
        </button>
        <button
          onClick={() => setShowExportUpsell(false)}
          style={{ background: 'transparent', color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(245,240,232,0.2)', padding: '0.55rem 1rem', borderRadius: '5px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer' }}
        >
          Maybe Later
        </button>
      </div>
    </div>
  )

  const FREQ = { monthly: 1, quarterly: 1/3, 'half-yearly': 1/6, yearly: 1/12 }
  const efMonthly =
    loans.reduce((s, l) => s + parseFloat(l.emi_amount || 0), 0) +
    (insurancePolicies || []).reduce((s, p) => s + parseFloat(p.premium_amount || 0) * (FREQ[p.premium_frequency] || 1), 0) +
    (expenses || []).reduce((s, e) => s + parseFloat(e.amount || 0) * (FREQ[e.frequency] || 1), 0)
  const efBalance = parseFloat(emergencyFund?.current_balance || 0)
  const efTarget  = parseFloat(emergencyFund?.target_amount) || efMonthly * (emergencyFund?.target_months || 6) || 0
  const efPct     = efTarget > 0 ? Math.min(100, Math.round((efBalance / efTarget) * 100)) : 0
  const efColor   = efPct >= 100 ? '#4a6741' : efPct >= 60 ? '#c9a84c' : '#b8441a'
  const efMonthsCovered = efMonthly > 0 ? efBalance / efMonthly : 0

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>

      <Sidebar
        activePage={activePage}
        onNavigate={setActivePage}
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        userName={userName}
        userAvatar={userAvatar}
        onSignOut={signOut}
        showAdmin={showAdmin}
        onOpenAdmin={onOpenAdmin}
        appSettings={appSettings}
        exportRef={exportRef}
        onExportClick={() => setShowExportMenu(v => !v)}
        showExportMenu={showExportMenu}
        exportMenuContent={exportMenuContent}
      />

      {/* Right panel */}
      <div style={{
        flex: 1,
        marginLeft: isMobile ? 0 : W,
        transition: 'margin-left 0.22s cubic-bezier(0.4,0,0.2,1)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>

        {/* Top header */}
        <header style={{
          position: 'sticky', top: 0, zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 1.5rem',
          height: '58px',
          background: 'var(--bg)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700', color: 'var(--text)' }}>
            {PAGE_TITLES[activePage] || 'Dashboard'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <ActionButton session={session} onNavigate={setActivePage} />
            <ClubhouseBell session={session} onClick={() => setActivePage('forum')} />

            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.4rem 0.45rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer', outline: 'none' }}
            >
              {Object.entries(CURRENCIES).map(([code, c]) => (
                <option key={code} value={code}>{c.symbol} {code}</option>
              ))}
            </select>

            <button
              onClick={toggleTheme}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{ background: 'transparent', color: isDark ? '#f5f0e8' : 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.44rem 0.6rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85rem', cursor: 'pointer', lineHeight: 1 }}
            >
              {isDark ? '☀' : '🌙'}
            </button>

            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications(v => !v)}
                title="Notifications"
                style={{ position: 'relative', background: 'transparent', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.44rem 0.55rem', cursor: 'pointer', lineHeight: 1, fontSize: '1rem', color: 'var(--text-muted)' }}
              >
                🔔
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '-6px', right: '-6px',
                    background: '#c0392b', color: '#fff',
                    borderRadius: '99px', minWidth: '17px', height: '17px',
                    fontSize: '0.6rem', fontFamily: 'monospace', fontWeight: '700',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 3px', lineHeight: 1, border: '2px solid var(--bg)'
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div style={{
                  position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  width: '320px', zIndex: 200, overflow: 'hidden'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.92rem', fontWeight: '700', color: 'var(--text)' }}>Notifications</span>
                    {unreadCount > 0 && (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', background: '#c0392b', color: '#fff', padding: '0.1rem 0.4rem', borderRadius: '99px' }}>
                        {unreadCount} unread
                      </span>
                    )}
                  </div>
                  <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                    {unreadMessages.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.75rem' }}>No new messages</div>
                    ) : (
                      unreadMessages.map((msg, i) => (
                        <div
                          key={msg.id}
                          onClick={() => dismissMessage(msg.id)}
                          style={{
                            padding: '0.85rem 1rem',
                            borderBottom: i < unreadMessages.length - 1 ? '1px solid var(--border)' : 'none',
                            cursor: 'pointer', transition: 'background 0.15s',
                            borderLeft: '3px solid #c9a84c'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hdr)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.3rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.3 }}>📢 {msg.title}</span>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', marginTop: '2px', flexShrink: 0 }}>tap to dismiss</span>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{msg.message}</div>
                        </div>
                      ))
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <div style={{ padding: '0.55rem 1rem', borderTop: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
                      <button
                        onClick={() => { unreadMessages.forEach(m => dismissMessage(m.id)); setShowNotifications(false) }}
                        style={{ background: 'none', border: 'none', fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', cursor: 'pointer', padding: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                      >
                        Mark all as read
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setEditingLoan(null)
                if (!isPaid && !isAdmin && loans.length >= 2) { setShowLoanLimitModal(true); return }
                setShowAddModal(true)
              }}
              style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.48rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              + Add Loan
            </button>
          </div>
        </header>

        <main style={{ flex: 1, maxWidth: '1300px', width: '100%', margin: '0 auto', padding: isMobile ? '1.5rem 1rem 5rem' : '2rem 1.5rem 4rem' }}>
          {activePage === 'dashboard' && (
            <>
              <MembershipCard session={session} onUpgradeSuccess={() => setIsPaid(true)} />
              <Summary loans={loans} emiPayments={emiPayments} currentMonth={currentMonth} />
              <div onClick={() => setActivePage('emergency')} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1rem 1.5rem', marginBottom: '1.5rem', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'box-shadow 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: efColor }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>🏦</span>
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Emergency Fund</div>
                      <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: '700', color: efColor, lineHeight: 1 }}>
                        {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(efBalance)}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                        covers <span style={{ color: efColor, fontWeight: '600' }}>{efMonthsCovered.toFixed(1)}</span> months · target {efTarget > 0 ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(efTarget) : 'not set'}
                      </div>
                    </div>
                  </div>
                  <div style={{ minWidth: '180px', flex: '1', maxWidth: '280px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                      <span>{efPct}% funded</span>
                      <span style={{ color: '#3d4a5c', fontSize: '0.6rem' }}>→ view fund</span>
                    </div>
                    <div style={{ height: '6px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: efPct + '%', background: efColor, borderRadius: '99px', transition: 'width 0.8s ease' }} />
                    </div>
                  </div>
                </div>
              </div>
              <HealthAndSuggestions loans={loans} emiPayments={emiPayments} currentMonth={currentMonth} isPaid={isPaid || isAdmin} onFeatureLock={(title, description) => setFeatureLock({ title, description })} />
            </>
          )}
          {activePage === 'forum' && <Forum session={session} />}
          {activePage === 'loans' && (
            <LoanList
              loans={loans}
              emiPayments={emiPayments}
              currentMonth={currentMonth}
              onEdit={(loan) => { setEditingLoan(loan); setShowAddModal(true) }}
              onDelete={deleteLoan}
              onSetEmi={setEmiStatus}
              loading={loading}
              userId={session.user.id}
              userPhone={profile?.phone_number}
              userName={userName}
              onLoanUpdated={fetchLoans}
              isPaid={isPaid || isAdmin}
              onFeatureLock={(title, description) => setFeatureLock({ title, description })}
            />
          )}
          {activePage === 'history' && (
            <PaymentHistory loans={loans} emiPayments={emiPayments} />
          )}
          {activePage === 'compare' && (
            <LoanComparison loans={loans} />
          )}
          {activePage === 'reminders' && (
            <Reminders loans={loans} emiPayments={emiPayments} currentMonth={currentMonth} />
          )}
          {activePage === 'insurance' && (
            <InsuranceList
              policies={insurancePolicies}
              payments={insurancePayments}
              claims={insuranceClaims}
              currentMonth={currentMonth}
              onAdd={() => { setEditingPolicy(null); setShowAddInsuranceModal(true) }}
              onEdit={(policy) => { setEditingPolicy(policy); setShowAddInsuranceModal(true) }}
              onDelete={deletePolicy}
              onSetPayment={setInsurancePaymentStatus}
              loading={loading}
            />
          )}
          {activePage === 'expenses' && (
            <Expenses
              expenses={expenses}
              expensePayments={expensePayments}
              loans={loans}
              insurancePolicies={insurancePolicies}
              onSave={saveExpense}
              onDelete={deleteExpense}
              onSetPayment={setExpensePayment}
              currentMonth={currentMonth}
              loading={loading}
            />
          )}
          {activePage === 'emergency' && (
            <EmergencyFund
              fund={emergencyFund}
              transactions={emergencyFundTx}
              loans={loans}
              insurancePolicies={insurancePolicies}
              expenses={expenses}
              onSaveFund={saveEmergencyFund}
              onAddTransaction={addEmergencyFundTransaction}
              loading={loading}
            />
          )}
          {activePage === 'investments' && (
            <Investments
              investments={investments}
              investmentHistory={investmentHistory}
              onSave={saveInvestment}
              onDelete={deleteInvestment}
              onUpdateValue={updateInvestmentValue}
              onLivePriceUpdate={updateInvestmentCurrentValue}
              loading={loading}
            />
          )}
          {activePage === 'networth' && (
            <NetWorth
              loans={loans}
              emergencyFund={emergencyFund}
              investments={investments}
              expenses={expenses}
              insurancePolicies={insurancePolicies}
              loading={loading}
            />
          )}
          {activePage === 'calculator' && <LoanCalculator />}
          {activePage === 'payoff' && (
            <SmartPayoffEngine
              loans={loans}
              isPaid={isPaid || isAdmin}
              onFeatureLock={(title, description) => setFeatureLock({ title, description })}
            />
          )}
          {activePage === 'budget' && (
            <BudgetPlanner loans={loans} insurancePolicies={insurancePolicies} expenses={expenses} />
          )}
          {activePage === 'credit' && (
            <CreditScoreTips loans={loans} emiPayments={emiPayments} />
          )}
          {activePage === 'profile' && (
            <Profile session={session} profile={profile} onProfileUpdate={fetchProfile} />
          )}
        </main>

        {/* Footer */}
        <footer style={{
          borderTop: '1px solid var(--border)',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.5rem',
          flexShrink: 0,
        }}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} DebtCrush · All rights reserved
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
            {/* Android App Download */}
            <a
              href="https://play.google.com/store/apps/details?id=com.debtcrush.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                background: '#0d0d0d', color: '#c9a84c',
                textDecoration: 'none', borderRadius: '5px',
                padding: '0.3rem 0.75rem', border: '1px solid rgba(201,168,76,0.3)',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#c9a84c'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'}
            >
              <span style={{ fontSize: '0.85rem' }}>📱</span>
              <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: '600', letterSpacing: '0.04em' }}>
                Android App
              </span>
            </a>
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.06em' }}
              onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            >
              Privacy Policy
            </a>
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.06em' }}
              onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            >
              Terms of Service
            </a>
            <a
              href="mailto:support@debtcrush.in"
              style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.06em' }}
              onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            >
              Support
            </a>
          </div>
        </footer>
      </div>

      {showAddModal && (
        <AddLoanModal
          loan={editingLoan}
          onSave={saveLoan}
          onClose={() => { setShowAddModal(false); setEditingLoan(null) }}
        />
      )}

      {showAddInsuranceModal && (
        <AddInsuranceModal
          policy={editingPolicy}
          onSave={savePolicy}
          onClose={() => { setShowAddInsuranceModal(false); setEditingPolicy(null) }}
        />
      )}

      {exportUpsellModal}

      {/* Loan limit modal — shown when free user tries to add a 3rd loan */}
      {showLoanLimitModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowLoanLimitModal(false) }}
          style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div style={{ background: '#0d0d0d', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', width: '100%', maxWidth: '400px', padding: '1.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.6rem' }}>🔒</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', color: '#f5f0e8', marginBottom: '0.5rem' }}>
              Free plan is limited to 2 loans
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.74rem', color: 'rgba(245,240,232,0.65)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
              You've reached the free loan limit. Upgrade to DebtCrush Premium (₹149/month) to track unlimited loans, get a personalised payoff plan, export reports, and more.
            </div>
            <button
              onClick={() => { setShowLoanLimitModal(false); setActivePage('dashboard') }}
              style={{ background: '#c9a84c', color: '#0d0d0d', border: 'none', padding: '0.55rem 1.3rem', borderRadius: '5px', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.03em', marginRight: '0.5rem' }}
            >
              Upgrade Now
            </button>
            <button
              onClick={() => setShowLoanLimitModal(false)}
              style={{ background: 'transparent', color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(245,240,232,0.2)', padding: '0.55rem 1rem', borderRadius: '5px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer' }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Generic feature lock modal */}
      {featureLock && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setFeatureLock(null) }}
          style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div style={{ background: '#0d0d0d', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', width: '100%', maxWidth: '400px', padding: '1.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.6rem' }}>🔒</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', color: '#f5f0e8', marginBottom: '0.5rem' }}>
              {featureLock.title}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.74rem', color: 'rgba(245,240,232,0.65)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
              {featureLock.description}
            </div>
            <button
              onClick={() => { setFeatureLock(null); setActivePage('dashboard') }}
              style={{ background: '#c9a84c', color: '#0d0d0d', border: 'none', padding: '0.55rem 1.3rem', borderRadius: '5px', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.03em', marginRight: '0.5rem' }}
            >
              Upgrade Now
            </button>
            <button
              onClick={() => setFeatureLock(null)}
              style={{ background: 'transparent', color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(245,240,232,0.2)', padding: '0.55rem 1rem', borderRadius: '5px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer' }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Gift Premium Celebration Popup */}
      {giftPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <style>{`
            @keyframes confettiFall {
              0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
              100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
            }
            @keyframes giftPop {
              0%   { transform: scale(0.5); opacity: 0; }
              70%  { transform: scale(1.08); opacity: 1; }
              100% { transform: scale(1);   opacity: 1; }
            }
            @keyframes sparkle {
              0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
              50%       { opacity: 1; transform: scale(1) rotate(180deg); }
            }
            @keyframes shimmer {
              0%   { background-position: -200% center; }
              100% { background-position:  200% center; }
            }
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50%       { transform: translateY(-8px); }
            }
            .gift-card { animation: giftPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
            .gift-emoji { animation: float 2s ease-in-out infinite; display: inline-block; }
            .sparkle-1 { animation: sparkle 1.5s ease-in-out 0.2s infinite; }
            .sparkle-2 { animation: sparkle 1.5s ease-in-out 0.5s infinite; }
            .sparkle-3 { animation: sparkle 1.5s ease-in-out 0.8s infinite; }
            .sparkle-4 { animation: sparkle 1.5s ease-in-out 1.1s infinite; }
            .shimmer-text {
              background: linear-gradient(90deg, #c9a84c, #f5d98b, #c9a84c, #f5d98b, #c9a84c);
              background-size: 200% auto;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              animation: shimmer 2s linear infinite;
            }
          `}</style>

          {/* Confetti pieces */}
          {[...Array(24)].map((_, i) => {
            const colors = ['#c9a84c', '#f5d98b', '#4a6741', '#b8441a', '#3d4a5c', '#f5f0e8']
            const shapes = ['8px', '50%', '2px']
            return (
              <div key={i} style={{
                position: 'fixed',
                left: (i * 4.2) % 100 + '%',
                top: '-20px',
                width: Math.random() > 0.5 ? '10px' : '8px',
                height: Math.random() > 0.5 ? '14px' : '10px',
                borderRadius: shapes[i % 3],
                background: colors[i % colors.length],
                animation: `confettiFall ${1.8 + (i % 5) * 0.4}s linear ${(i % 8) * 0.2}s infinite`,
                opacity: 0.9,
                pointerEvents: 'none',
              }} />
            )
          })}

          <div className="gift-card" style={{ background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1410 100%)', border: '1px solid rgba(201,168,76,0.5)', borderRadius: '20px', width: '100%', maxWidth: '420px', padding: '2.5rem 2rem', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: '0 0 60px rgba(201,168,76,0.2), 0 20px 60px rgba(0,0,0,0.4)' }}>

            {/* Corner sparkles */}
            <span className="sparkle-1" style={{ position: 'absolute', top: '16px', left: '20px', fontSize: '1.1rem' }}>✦</span>
            <span className="sparkle-2" style={{ position: 'absolute', top: '16px', right: '20px', fontSize: '1.1rem', color: '#c9a84c' }}>✦</span>
            <span className="sparkle-3" style={{ position: 'absolute', bottom: '60px', left: '16px', fontSize: '0.9rem', color: '#f5d98b' }}>✦</span>
            <span className="sparkle-4" style={{ position: 'absolute', bottom: '60px', right: '16px', fontSize: '0.9rem' }}>✦</span>

            {/* Glow ring behind emoji */}
            <div style={{ width: '96px', height: '96px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.3) 0%, transparent 70%)', margin: '0 auto 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="gift-emoji" style={{ fontSize: '3.5rem', lineHeight: 1 }}>🎁</span>
            </div>

            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(201,168,76,0.7)', marginBottom: '0.5rem' }}>
              ✦ &nbsp; Congratulations &nbsp; ✦
            </div>

            <div className="shimmer-text" style={{ fontFamily: 'Georgia,serif', fontSize: '1.6rem', fontWeight: '700', marginBottom: '0.75rem', lineHeight: 1.2 }}>
              You've Been Gifted<br />DebtCrush Premium!
            </div>

            <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'rgba(245,240,232,0.6)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
              {giftPopup.message}
            </div>

            {/* Features grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1.5rem' }}>
              {['🎯 Smart Payoff Engine', '📊 PDF Export', '💰 Unlimited Loans', '💡 Extra Payment Simulator'].map(f => (
                <div key={f} style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px', padding: '0.6rem 0.75rem', fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(245,240,232,0.75)', textAlign: 'left' }}>
                  {f}
                </div>
              ))}
            </div>

            <button
              onClick={() => setGiftPopup(null)}
              style={{ background: 'linear-gradient(135deg, #c9a84c, #f5d98b, #c9a84c)', backgroundSize: '200% auto', color: '#0d0d0d', border: 'none', padding: '0.85rem 2.5rem', borderRadius: '50px', fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', animation: 'shimmer 2s linear infinite', width: '100%' }}
            >
              ✦ Start Exploring Premium
            </button>
          </div>
        </div>
      )}

      <OnboardingPrompt
        session={session}
        loans={loans}
        expenses={expenses}
        emergencyFund={emergencyFund}
        insurancePolicies={insurancePolicies}
        investments={investments}
        membershipTier={isPaid ? 'premium' : 'free'}
        onNavigate={setActivePage}
      />

    </div>
  )
}